import HomePage from 'components/pages/Home';
import { DEFAULT_PILLS } from 'components/pages/Home/HomePills';

import { fetchHomePills } from 'lib/homePills';
import { toAbsoluteLink } from 'lib/links';
import { resolvify } from 'lib/promises';
import { removeWhitespaceBetweenCharacters } from 'lib/string';

import { getAds } from 'services/ads';
import { getCategoriesList } from 'services/category';
import { getCouponFeaturedStoresList, getCouponsList } from 'services/coupon';
import { getLandingsList } from 'services/landing';
import { getFeaturedOffersList, getOffersList } from 'services/offer';
import { getStoresList } from 'services/store';
import { getPasswordRecoveryHashValidation } from 'services/user';

import APP from 'constants/app';
import COOKIES from 'constants/cookies';
import LINKS from 'constants/links';

const CANONICAL_URL = toAbsoluteLink(LINKS.HOME);
const OFFERS_SERVER_LIMIT = 12;
const PAGE_H1 = 'Ofertas em destaque';
const TAB_NAME = 'highlights';

const Index = ({
  cardsAds,
  isUserLogged,
  landscapeAds,
  recoveryPassword,
  serverCoupons,
  serverFeaturedOffers,
  serverFeaturedStores,
  serverOffers,
  serverHomePills,
  serverHomePillsTitle,
}) => (
  <HomePage
    canonicalUrl={CANONICAL_URL}
    cardsAds={cardsAds}
    h1Text={PAGE_H1}
    isUserLogged={isUserLogged}
    landscapeAds={landscapeAds}
    recoveryPassword={recoveryPassword}
    serverCoupons={serverCoupons}
    serverFeaturedOffers={serverFeaturedOffers}
    serverFeaturedStores={serverFeaturedStores}
    serverHomePills={serverHomePills}
    serverHomePillsTitle={serverHomePillsTitle}
    serverOffers={serverOffers}
  />
);

export const getServerSideProps = async ({ query, req }) => {
  try {
    const {
      ha: recoveryHash = null,
      em: rawUserEmail = null,
      pass_rec: isPasswordRecoveryMode,
    } = query;
    /**
     * Pula a busca SSR de offers quando o user está logado: sem o token (que
     * fica no localStorage e não é acessível server-side), o backend cairia no
     * caminho anônimo e renderizaria o feed deslogado no first paint —
     * exatamente o que faz "ofertas iguais entre logado/deslogado". O cookie
     * `is_user_logged` é o indicador que temos no SSR; a busca real do feed
     * personalizado roda no cliente após hydration, com o Bearer aplicado pelo
     * `apiRequest` (`OffersList` agora habilita o useInfiniteQuery quando
     * serverOffers vem null e exibe skeleton durante o initial loading).
     *
     * Contrato load-bearing entre cookie e token (mexer com cuidado):
     * - `UserProvider.login` (providers/UserProvider.js) seta o cookie
     *   atomicamente com o ACCESS_TOKEN no localStorage.
     * - `clearLoginStorage` (lib/user/index.js) apaga o cookie junto com
     *   o token no logout.
     * - Janela de inconsistência possível: token expirado (JWT TTL) mas
     *   cookie sobrevivendo (AUTH_EXPIRATION_TIME). User cai aqui como
     *   "logado", SSR pula offers, e o fetch client-side com Bearer
     *   expirado retorna 401 — feed fica vazio em vez de mostrar anônimo.
     *   Aceito por enquanto (ocorre só no boundary de expiração); fix
     *   definitivo seria validar o token (não só presença do cookie).
     */
    const isUserLogged = req.cookies?.[COOKIES.IS_USER_LOGGED] === 'true';
    // URL-encoded "+" in email query params arrives as a space — strip whitespace before validation.
    const userEmail = rawUserEmail
      ? removeWhitespaceBetweenCharacters(rawUserEmail.trim())
      : null;
    const isPasswordRecoveryModeEnabled = !!(
      isPasswordRecoveryMode &&
      recoveryHash &&
      userEmail
    );

    const [
      serverOffers,
      serverFeaturedOffers,
      serverCoupons,
      serverFeaturedStores,
      categories,
      stores,
      featuredLandings,
      highlightedLandings,
      {
        data: {
          cardAd: cardsAds = [],
          homeAd: landscapeAds = [],
          topAd: [topbarAd = null],
          topbarHighlight: navbarAds,
        } = {},
      },
      passwordRecovery,
    ] = await resolvify([
      isUserLogged
        ? { fn: () => null, fallback: null }
        : {
            fn: () => getOffersList({ limit: OFFERS_SERVER_LIMIT, ...query }),
            fallback: null,
          },
      { fn: getFeaturedOffersList(), fallback: getFeaturedOffersList.fallback },
      {
        fn: getCouponsList({ limit: 4 }),
        fallback: [],
      },
      {
        fn: getCouponFeaturedStoresList(),
        fallback: getCouponFeaturedStoresList.fallback,
      },
      {
        fn: getCategoriesList({ selectedCategories: true }),
        fallback: getCategoriesList.fallback,
      },
      {
        fn: getStoresList({ from: 'topbar' }),
        fallback: getStoresList.fallback,
      },
      {
        fn: getLandingsList({ featured: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getLandingsList({ highlighted: true }),
        fallback: getLandingsList.fallback,
      },
      {
        fn: getAds(),
        fallback: getAds.fallback,
      },
      {
        fn: () =>
          getPasswordRecoveryHashValidation({ recoveryHash, userEmail }),
        fallback: getPasswordRecoveryHashValidation.fallback,
        enabled: isPasswordRecoveryModeEnabled,
      },
    ]);

    let recoveryPassword = null;

    if (passwordRecovery?.isHashValid) {
      recoveryPassword = {
        recoveryHash,
        userEmail,
        isValid: true,
      };
    }

    const { serverHomePills, serverHomePillsTitle } = await fetchHomePills();

    return {
      props: {
        cardsAds,
        isUserLogged,
        landscapeAds,
        recoveryPassword,
        serverCoupons,
        serverFeaturedOffers,
        serverFeaturedStores,
        serverOffers,
        serverHomePills,
        serverHomePillsTitle,
        [APP.CTX_OPTIONAL_PROPS]: {
          activeOffersListTab: TAB_NAME,
          categories,
          featuredLandings: Array.isArray(featuredLandings)
            ? {}
            : featuredLandings,
          highlightedLandings: Array.isArray(highlightedLandings)
            ? {}
            : highlightedLandings,
          navbarAds,
          stores,
          topbarAd,
        },
      },
    };
  } catch {
    return {
      props: {
        cardsAds: [],
        isUserLogged: false,
        landscapeAds: [],
        recoveryPassword: null,
        serverCoupons: [],
        serverFeaturedOffers: [],
        serverFeaturedStores: [],
        serverOffers: { after: '', before: null, offers: [] },
        serverHomePills: DEFAULT_PILLS,
        serverHomePillsTitle: null,
        [APP.CTX_OPTIONAL_PROPS]: {
          activeOffersListTab: TAB_NAME,
          categories: [],
          featuredLandings: { landings: [] },
          highlightedLandings: { landings: [] },
          navbarAds: [],
          stores: [],
          topbarAd: null,
        },
      },
    };
  }
};

export default Index;
